import uniqWith from "lodash/uniqWith";
import isEqual from "lodash/isEqual";

import { LocalStorageSettingsI } from "@/lib/types/dialer/local-storage-settings";
import { CustomMeetingsToastI } from "@/lib/types/custom-toasts/meetings";

import { GetListsRequestFilterParamsI } from "@/api/routes/list";
import { CreateNewLeadFormI } from "@/modals/create-new-lead-modal/new-lead-form";
import { AccountDetailsViewOptionI } from "@/components/modules/pipeline/account-details/nav/view-options/interface";
import { ColumnSizingState } from "@tanstack/react-table";
import { ListSubsectionI } from "shared/lib/interfaces/lists";
import { CHAT_ID_STORAGE_KEY } from "@/modules/messages/context";

const AUTH0_TOKEN_UPDATED_AT = "auth0_token_updated_at";
const CUSTOM_MEETINGS_TOASTS = "custom_meetings_toasts";
const CALLING_MODULE_SETTINGS = "calling_module_settings";
const DIALER_TOUR_COMPLETION_DATE = "dialer_tour_completion_date";
const MESSAGES_INTRO_MODAL_VISITED_DATE = "messages_intro_modal_visited_date";
const DILAER_VISITED_AT = "dialer_visited_at";
const NOTIFICATIONS_INTRO_MODAL_VISITED_DATE =
  "notifications_intro_modal_visited_date";

const ACCOUNT_DETAILS_BACK_TO = "account_details_back_to";
const ACTIVE_LIST_ID = "active_list_id";
const ACTIVE_LIST_SUBSECTION = "active_list_subsection";

const DIALER_GLOBAL_MAX_LEADS_DIALING = "dialer_global_max_leads_dialing";

/**
 * @deprecated
 */
const LISTS_TABLE_COLUMNS_VISIBILITY_DEPRECATED =
  "lists_table_columns_visibility";

const LISTS_TABLE_COLUMNS_VISIBILITY = "lists_table_visible_columns";
const LIST_SUBSECTION_INTRO_COMPLETED_DATE =
  "list_subsection_intro_completed_date";

/**
 * Should follow up by list id
 */
const LIST_FILTERS = "list_filters";

const PERFORMANCE_DASHBOARD_TIME_FREQUENCY_FILTER =
  "performance_dashboard_time_frequency_filter";

const PERFORMANCE_DASHBOARD_TIME_RANGE_FILTER =
  "performance_dashboard_time_range_filter";

const PERFORMANCE_DASHBOARD_CAMPAIGN_FILTER =
  "performance_dashboard_campaign_filter";

const GOAL_TRACKER_SHOWN_AFTER_FIRST_CALL =
  "goal_tracker_shown_after_first_call";

const ADD_NEW_LEAD_MODAL_PERSISTED_VALUES = "add_new_lead_modal";

const SEND_EMAIL_MODAL_PERSISTED_CONTENT = "email_modal_persisted_content";

const ACCOUNT_DETAILS_VIEW_OPTIONS = "account_details_view_options";

/**
 * NOTE
 * We save label only as id can change
 */
const PREFERRED_AUDIO_INPUT = "preferred_audio_input";
const PREFERRED_AUDIO_OUTPUT = "preferred_audio_output";

const APP_VERSION_LAST_RELOAD_AT = "app_version_last_reload_at";

const LIST_WORKSPACE_COLUMN_SIZING = "list_workspace_column_sizing";

const LIST_TOUR_MODAL_VISITED_DATE = "list_tour_modal_visited_date";

const LIST_TOUR_COMPLETION_DATE = "list_tour_completion_date";

// Works ONLY in the browser
export class LocalStorage {
  get auth0TokenUpdatedAt(): string {
    return window.localStorage.getItem(AUTH0_TOKEN_UPDATED_AT) as string;
  }
  set auth0TokenUpdatedAt(date: string) {
    window.localStorage.setItem(AUTH0_TOKEN_UPDATED_AT, date);
  }

  get customMeetingsToasts(): any | undefined {
    const storedToasts = window.localStorage.getItem(CUSTOM_MEETINGS_TOASTS);
    let parsedStoredToasts;

    try {
      parsedStoredToasts = JSON.parse(storedToasts as string);
    } catch {
      parsedStoredToasts = [];
    }

    return parsedStoredToasts;
  }
  set customMeetingsToasts(customMeetingToasts: CustomMeetingsToastI[]) {
    const uniqueCustomMeetingToasts = uniqWith(
      customMeetingToasts || [],
      isEqual
    );

    window.localStorage.setItem(
      CUSTOM_MEETINGS_TOASTS,
      JSON.stringify(uniqueCustomMeetingToasts)
    );
  }

  get callingModuleSettings(): LocalStorageSettingsI {
    const settings = window.localStorage.getItem(CALLING_MODULE_SETTINGS) || "";
    let parsedSettings;

    try {
      parsedSettings = JSON.parse(settings as string);
    } catch {
      parsedSettings = {};
    }

    return parsedSettings;
  }
  set callingModuleSettings(settings: LocalStorageSettingsI) {
    window.localStorage.setItem(
      CALLING_MODULE_SETTINGS,
      JSON.stringify(settings)
    );
  }

  get dialerTourCompletionDate(): string {
    return window.localStorage.getItem(DIALER_TOUR_COMPLETION_DATE) as string;
  }
  set dialerTourCompletionDate(date: string) {
    window.localStorage.setItem(DIALER_TOUR_COMPLETION_DATE, date);
  }

  get messagesIntroModalCompletionDate(): string {
    return window.localStorage.getItem(
      MESSAGES_INTRO_MODAL_VISITED_DATE
    ) as string;
  }
  set messagesIntroModalCompletionDate(date: string) {
    window.localStorage.setItem(MESSAGES_INTRO_MODAL_VISITED_DATE, date);
  }

  get notificationIntroModalCompletionDate(): string {
    return window.localStorage.getItem(
      NOTIFICATIONS_INTRO_MODAL_VISITED_DATE
    ) as string;
  }
  set notificationIntroModalCompletionDate(date: string) {
    window.localStorage.setItem(NOTIFICATIONS_INTRO_MODAL_VISITED_DATE, date);
  }

  get dialerVisitedAt(): string {
    return window.localStorage.getItem(DILAER_VISITED_AT) as string;
  }
  set dialerVisitedAt(date: string) {
    window.localStorage.setItem(DILAER_VISITED_AT, date);
  }

  get hasDeprecatedColumnsVisibility(): boolean {
    return !!window.localStorage.getItem(
      LISTS_TABLE_COLUMNS_VISIBILITY_DEPRECATED
    );
  }

  get listsTableColumnsVisibility(): Record<string, boolean> | null {
    /**
     * TODO remove this after some time.
     *
     * This is a temporary solution to migrate from the old storage key to the
     * new one to avoid conflicting settings.
     */
    if (this.hasDeprecatedColumnsVisibility) {
      window.localStorage.removeItem(LISTS_TABLE_COLUMNS_VISIBILITY_DEPRECATED);
    }

    const storedColumns = window.localStorage.getItem(
      LISTS_TABLE_COLUMNS_VISIBILITY
    );

    let parsedColumns;

    try {
      parsedColumns = JSON.parse(storedColumns as string);
    } catch {
      parsedColumns = [];
    }

    return parsedColumns;
  }
  set listsTableColumnsVisibility(columns: Record<string, boolean> | null) {
    if (columns) {
      window.localStorage.setItem(
        LISTS_TABLE_COLUMNS_VISIBILITY,
        JSON.stringify(columns)
      );
    } else {
      window.localStorage.removeItem(LISTS_TABLE_COLUMNS_VISIBILITY);
    }
  }

  get accountDetailsBackTo(): string {
    return window.localStorage.getItem(ACCOUNT_DETAILS_BACK_TO) as string;
  }
  set accountDetailsBackTo(backTo: string | null | undefined) {
    if (backTo) {
      window.localStorage.setItem(ACCOUNT_DETAILS_BACK_TO, backTo);
    } else {
      window.localStorage.removeItem(ACCOUNT_DETAILS_BACK_TO);
    }
  }

  get activeListId(): string {
    return window.localStorage.getItem(ACTIVE_LIST_ID) as string;
  }
  set activeListId(listId: string | null | undefined) {
    if (listId) {
      window.localStorage.setItem(ACTIVE_LIST_ID, listId);
    } else {
      window.localStorage.removeItem(ACTIVE_LIST_ID);
    }
  }

  get activeListSubsection(): ListSubsectionI {
    return window.localStorage.getItem(
      ACTIVE_LIST_SUBSECTION
    ) as ListSubsectionI;
  }
  set activeListSubsection(subsection: ListSubsectionI | null | undefined) {
    if (subsection) {
      window.localStorage.setItem(ACTIVE_LIST_SUBSECTION, subsection as string);
    } else {
      window.localStorage.removeItem(ACTIVE_LIST_SUBSECTION);
    }
  }

  get dialerGlobalMaxLeadsDialing(): number | undefined {
    return (
      parseInt(
        window.localStorage.getItem(DIALER_GLOBAL_MAX_LEADS_DIALING) || ""
      ) || undefined
    );
  }
  set dialerGlobalMaxLeadsDialing(value: number) {
    window.localStorage.setItem(
      DIALER_GLOBAL_MAX_LEADS_DIALING,
      value.toString()
    );
  }

  getListFilters(listId: string): GetListsRequestFilterParamsI {
    try {
      return JSON.parse(
        window.localStorage.getItem(`${LIST_FILTERS}-${listId}`) as string
      );
    } catch {
      return {};
    }
  }

  setListFilters(listId: string, filters?: GetListsRequestFilterParamsI): void {
    window.localStorage.setItem(
      `${LIST_FILTERS}-${listId}`,
      JSON.stringify(filters || {})
    );
  }

  resetAllListFilters() {
    Object.keys(window.localStorage)?.forEach((key) => {
      if (key.startsWith(LIST_FILTERS)) {
        window.localStorage.removeItem(key);
      }
    });
  }

  get performanceDashboardTimeFrequencyFilter(): string {
    return window.localStorage.getItem(
      PERFORMANCE_DASHBOARD_TIME_FREQUENCY_FILTER
    ) as string;
  }
  set performanceDashboardTimeFrequencyFilter(filter: string) {
    window.localStorage.setItem(
      PERFORMANCE_DASHBOARD_TIME_FREQUENCY_FILTER,
      filter
    );
  }

  get performanceDashboardTimeRangeFilter(): string {
    return window.localStorage.getItem(
      PERFORMANCE_DASHBOARD_TIME_RANGE_FILTER
    ) as string;
  }
  set performanceDashboardTimeRangeFilter(filter: string) {
    window.localStorage.setItem(
      PERFORMANCE_DASHBOARD_TIME_RANGE_FILTER,
      filter
    );
  }

  get performanceDashboardCampaignsFilter(): string[] {
    const storedCampaigns = window.localStorage.getItem(
      PERFORMANCE_DASHBOARD_CAMPAIGN_FILTER
    );

    if (!storedCampaigns) {
      return [];
    }

    try {
      return JSON.parse(storedCampaigns);
    } catch {
      return [];
    }
  }
  set performanceDashboardCampaignsFilter(filter: string[]) {
    window.localStorage.setItem(
      PERFORMANCE_DASHBOARD_CAMPAIGN_FILTER,
      JSON.stringify(filter)
    );
  }

  get goalTrackerShownAfterFirstCall(): string {
    return window.localStorage.getItem(
      GOAL_TRACKER_SHOWN_AFTER_FIRST_CALL
    ) as string;
  }
  set goalTrackerShownAfterFirstCall(value: string) {
    window.localStorage.setItem(GOAL_TRACKER_SHOWN_AFTER_FIRST_CALL, value);
  }

  get listSubsectionIntroCompletedAt(): string | null {
    return window.localStorage.getItem(LIST_SUBSECTION_INTRO_COMPLETED_DATE);
  }
  set listSubsectionIntroCompletedAt(date: string) {
    window.localStorage.setItem(LIST_SUBSECTION_INTRO_COMPLETED_DATE, date);
  }

  get newLeadModalPersistedValues(): Partial<CreateNewLeadFormI> {
    const storedValues = window.localStorage.getItem(
      ADD_NEW_LEAD_MODAL_PERSISTED_VALUES
    );
    let parsedValues;

    try {
      parsedValues = JSON.parse(storedValues as string);
    } catch {
      parsedValues = {};
    }

    return parsedValues;
  }
  set newLeadModalPersistedValues(
    values: Partial<CreateNewLeadFormI> | null | undefined
  ) {
    if (!values) {
      window.localStorage.removeItem(ADD_NEW_LEAD_MODAL_PERSISTED_VALUES);
      return;
    }

    window.localStorage.setItem(
      ADD_NEW_LEAD_MODAL_PERSISTED_VALUES,
      JSON.stringify(values)
    );
  }

  set emailModalContent(content: string | null | undefined) {
    if (!content) {
      window.localStorage.removeItem(SEND_EMAIL_MODAL_PERSISTED_CONTENT);
      return;
    }

    window.localStorage.setItem(SEND_EMAIL_MODAL_PERSISTED_CONTENT, content);
  }
  get emailModalContent(): string {
    return window.localStorage.getItem(
      SEND_EMAIL_MODAL_PERSISTED_CONTENT
    ) as string;
  }

  get preferredAudioInput(): string | null {
    return window.localStorage.getItem(PREFERRED_AUDIO_INPUT);
  }
  set preferredAudioInput(label: string) {
    window.localStorage.setItem(PREFERRED_AUDIO_INPUT, label);
  }

  get preferredAudioOutput(): string | null {
    return window.localStorage.getItem(PREFERRED_AUDIO_OUTPUT);
  }
  set preferredAudioOutput(label: string) {
    window.localStorage.setItem(PREFERRED_AUDIO_OUTPUT, label);
  }

  get accountDetailsViewOptions(): AccountDetailsViewOptionI[] | undefined {
    let options;

    try {
      options = JSON.parse(
        window.localStorage.getItem(ACCOUNT_DETAILS_VIEW_OPTIONS) as string
      );

      return options;
    } catch {
      return options;
    }
  }

  set accountDetailsViewOptions(options: AccountDetailsViewOptionI[]) {
    window.localStorage.setItem(
      ACCOUNT_DETAILS_VIEW_OPTIONS,
      JSON.stringify(options)
    );
  }

  get appVersionLastReloadAt(): string | undefined {
    return window.localStorage.getItem(APP_VERSION_LAST_RELOAD_AT) as string;
  }
  set appVersionLastReloadAt(date: string) {
    window.localStorage.setItem(APP_VERSION_LAST_RELOAD_AT, date);
  }

  get listWorkspaceColumnSizing(): ColumnSizingState | undefined {
    try {
      return JSON.parse(
        window.localStorage.getItem(`${LIST_WORKSPACE_COLUMN_SIZING}`) as string
      );
    } catch {
      return {};
    }
  }

  set listWorkspaceColumnSizing(sizing: ColumnSizingState) {
    window.localStorage.setItem(
      `${LIST_WORKSPACE_COLUMN_SIZING}`,
      JSON.stringify(sizing)
    );
  }

  get listTourModalVisitedDate(): string {
    return window.localStorage.getItem(LIST_TOUR_MODAL_VISITED_DATE) as string;
  }
  set listTourModalVisitedDate(date: string) {
    window.localStorage.setItem(LIST_TOUR_MODAL_VISITED_DATE, date);
  }

  get listTourCompletionDate(): string {
    return window.localStorage.getItem(LIST_TOUR_COMPLETION_DATE) as string;
  }
  set listTourCompletionDate(date: string) {
    window.localStorage.setItem(LIST_TOUR_COMPLETION_DATE, date);
  }

  clearItemsOnLogout() {
    this.activeListId = null;
    window.localStorage.removeItem(CHAT_ID_STORAGE_KEY);
  }
}
